import type { ReactElement } from "react";
import { KIT_METRICS, logical } from "./metrics.ts";

/**
 * The busy mark a control shows while it waits (brief section 5.1, "a busy button shows it is waiting without
 * changing size"): three dots, two logical pixels square with two between, that light in turn, drawn over the
 * control's label, which stays in place hidden so the control keeps its size. Buttons and menu items share it.
 */

/**
 * Where the busy dots start across a control whose width is not known until layout: the middle of its
 * padding box less half the dots, rounded down to a whole logical pixel by CSS `round()`.
 *
 * @returns The CSS `left` value.
 */
function centredDots(): string {
  return `round(down, calc(50% - ${logical(Math.floor(KIT_METRICS.busy.width / 2))}), var(--px))`;
}

/**
 * Three dots that blink in turn while a control waits, placed on whole logical pixels, in the middle of the
 * control across unless a left offset is given.
 *
 * @param props Where the dots sit inside the control's padding box: their top, and their left when the
 *   control's width is not what sets it (a button of known width, or a menu item whose label starts at a
 *   known offset).
 * @returns The dots, hidden from assistive technology, which reads `aria-busy` instead.
 */
export function BusyDots({ top, left }: { readonly top: number; readonly left?: number }): ReactElement {
  const dot = { width: logical(KIT_METRICS.busy.dot), height: logical(KIT_METRICS.busy.dot) };
  return (
    <span className="ot-busy" aria-hidden="true" style={{ top: logical(top), left: left === undefined ? centredDots() : logical(left), width: logical(KIT_METRICS.busy.width), gap: logical(KIT_METRICS.busy.gap) }}>
      <span className="ot-busy__dot" style={dot} />
      <span className="ot-busy__dot" style={dot} />
      <span className="ot-busy__dot" style={dot} />
    </span>
  );
}
